/**
 * Project class for managing Orgo projects
 */

import { ApiClient } from './api/client';
import type { ProjectInfo, ComputerInfo } from './types';
import { v4 as uuidv4 } from 'uuid';
import { getApiKey } from './utils/auth';

export class Project {
  private apiKey: string;
  private baseApiUrl?: string;
  private api: ApiClient;
  public name: string;
  public id!: string;
  private _info!: ProjectInfo;

  private constructor(
    name: string,
    apiKey: string,
    baseApiUrl?: string
  ) {
    this.name = name;
    this.apiKey = apiKey;
    this.baseApiUrl = baseApiUrl;
    this.api = new ApiClient(this.apiKey, this.baseApiUrl);
  }

  /**
   * Create or connect to a Project
   * 
   * @param options Configuration options
   * @param options.name Project name. If exists, connects to it. If not, creates it.
   * @param options.apiKey Orgo API key (defaults to ORGO_API_KEY env var)
   * @param options.baseApiUrl Custom API URL (optional)
   */
  public static async create(options?: {
    name?: string;
    apiKey?: string;
    baseApiUrl?: string;
  }): Promise<Project> {
    const apiKey = options?.apiKey || getApiKey() || '';
    const name = options?.name || `project-${uuidv4().substring(0, 8)}`;
    
    const project = new Project(name, apiKey, options?.baseApiUrl);
    await project._initializeProject();
    
    return project;
  }

  private async _initializeProject(): Promise<void> {
    try {
      // Try to get existing project
      const projectInfo = await this.api.getProjectByName(this.name);
      this.id = projectInfo.id;
      this._info = projectInfo;
    } catch (error) {
      // Project doesn't exist, create it
      const projectInfo = await this.api.createProject(this.name);
      this.id = projectInfo.id;
      this._info = projectInfo;
    }
  }

  public get info(): ProjectInfo {
    return this._info;
  }

  public async status(): Promise<ProjectInfo> {
    const projectInfo = await this.api.getProject(this.id);
    this._info = projectInfo;
    return projectInfo;
  }

  public async destroy(): Promise<unknown> {
    return this.api.deleteProject(this.id);
  }

  public async listComputers(): Promise<ComputerInfo[]> {
    return this.api.listComputers(this.id);
  }

  public async getComputer(computerName?: string): Promise<ComputerInfo | null> {
    const computers = await this.listComputers();
    if (!computers || computers.length === 0) {
      return null;
    }

    if (computerName) {
      const computer = computers.find(c => c.name === computerName);
      return computer ?? null;
    }

    return computers[0] ?? null;
  }
}